import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Card, Pill, colors } from "../ui/index.js";
import { venueShortName, venueCourtCount } from "../../lib/venue-names.js";
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dayCellStyle = {
    display: "grid",
    gridTemplateColumns: "44px 1fr",
    gap: 8,
    alignItems: "start",
    padding: "8px 10px",
    borderTop: `1px solid ${colors.divider}`,
    fontSize: "0.8rem",
};
export function PermissionsView({ permissions }) {
    // Permissions can contain the same venue under multiple casings (e.g.
    // "josc" and "JOSC") because earlier admin flows didn't normalize the
    // venueId. Collapse to one entry per UPPER(id), preferring the most
    // permissive (allowed > not allowed, the richer windowsByDay wins).
    const merged = new Map();
    for (const [rawId, perm] of Object.entries(permissions.venues)) {
        const key = rawId.toUpperCase();
        const existing = merged.get(key);
        if (!existing) {
            merged.set(key, perm);
            continue;
        }
        merged.set(key, {
            allowed: existing.allowed || perm.allowed,
            windowsByDay: Object.keys(perm.windowsByDay).length > Object.keys(existing.windowsByDay).length
                ? perm.windowsByDay
                : existing.windowsByDay,
        });
    }
    const venueIds = Array.from(merged.keys()).sort();
    if (venueIds.length === 0) {
        return (_jsx(Card, { children: _jsx("p", { style: { margin: 0, color: colors.textSecondary }, children: "No venues are currently allowed for this tenant. Contact the administrator to request access." }) }));
    }
    return (_jsx("div", { style: { display: "grid", gap: 12 }, children: venueIds.map((venueId) => {
            const perm = merged.get(venueId);
            if (!perm)
                return null;
            const allowedColor = perm.allowed ? colors.success : colors.danger;
            const allowedText = perm.allowed ? "Allowed" : "Not allowed";
            return (_jsxs(Card, { children: [_jsx("div", { style: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            marginBottom: 8,
                            flexWrap: "wrap",
                        }, children: _jsxs("div", { style: {
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                            }, children: [_jsx("strong", { style: { fontSize: "0.95rem" }, children: venueShortName(venueId) }), _jsx(Pill, { color: allowedColor, children: allowedText })] }) }), perm.allowed && (_jsx("div", { style: {
                            border: `1px solid ${colors.border}`,
                            borderRadius: 8,
                            overflow: "hidden",
                            background: colors.surface,
                        }, children: DAY_LABELS.map((label, idx) => {
                            const dayIdx = idx;
                            const windows = perm.windowsByDay[dayIdx] ?? [];
                            return (_jsxs("div", { style: dayCellStyle, children: [_jsx("span", { style: {
                                            fontWeight: 600,
                                            color: colors.textSecondary,
                                        }, children: label }), windows.length === 0 ? (_jsx("span", { style: { color: colors.textTertiary }, children: "\u2014" })) : (_jsx("span", { style: {
                                            display: "flex",
                                            gap: 6,
                                            flexWrap: "wrap",
                                        }, children: windows.map((w, i) => (_jsx(WindowCard, { from: w.from, to: w.to, cap: w.maxCourts ?? 1, total: Math.max(venueCourtCount(venueId), maxCapacityForVenue(perm)) }, `${w.from}-${w.to}-${i}`))) }))] }, dayIdx));
                        }) }))] }, venueId));
        }) }));
}
/**
 * Stacked window card: time on top, capacity dots + N/total below. Tooltip
 * spells out 'you can book up to N courts in parallel'. Total dots equal
 * the venue-wide maximum so rows align visually across windows.
 */
function WindowCard({ from, to, cap, total, }) {
    const tip = `You can book up to ${cap} court${cap === 1 ? "" : "s"} in parallel in this window.`;
    return (_jsxs("span", { title: tip, style: {
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "6px 12px",
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            background: colors.surface,
            minWidth: 130,
        }, children: [_jsxs("span", { style: {
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: colors.text,
                    fontVariantNumeric: "tabular-nums",
                }, children: [from, " \u2013 ", to] }), _jsxs("span", { style: {
                    display: "inline-flex",
                    gap: 3,
                    marginTop: 4,
                    alignItems: "center",
                }, children: [Array.from({ length: total }, (_, i) => (_jsx("span", { "aria-hidden": true, style: {
                            width: 12,
                            height: 8,
                            borderRadius: 2,
                            background: i < cap ? colors.primary : colors.border,
                        } }, i))), _jsxs("span", { style: {
                            color: colors.textTertiary,
                            fontSize: "0.7rem",
                            marginLeft: 4,
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                        }, children: [cap, " court", cap === 1 ? "" : "s"] })] })] }));
}
/**
 * Total dots to render — the highest per-window court cap across all days
 * (so dots never under-represent), with a floor of 1.
 */
function maxCapacityForVenue(perm) {
    let max = 1;
    for (const wins of Object.values(perm.windowsByDay)) {
        for (const w of wins ?? []) {
            if (typeof w.maxCourts === "number" && w.maxCourts > max)
                max = w.maxCourts;
        }
    }
    return Math.max(1, max);
}
